import { ReactNode, useEffect, useRef, useState } from "react";
import { X, AlertTriangle } from "lucide-react";

/** Base modal. Handles: overlay click-to-close, ESC-to-close, body-scroll
 *  lock, autofocus on the container. Keep the visual chrome consistent so
 *  every dialog in the app has the same feel. */
export function Modal({
  open, onClose, title, subtitle, children, footer, size = "md", closeOnOverlay = true,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Small line under the title (status pills, ids…). */
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  closeOnOverlay?: boolean;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    // Move focus into the dialog so keyboard/screen-reader users land inside.
    requestAnimationFrame(() => dialogRef.current?.focus());
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  const widths = { sm: "sm:max-w-sm", md: "sm:max-w-md", lg: "sm:max-w-2xl", xl: "sm:max-w-3xl" };

  return (
    <div
      // Phones: a sheet from the bottom edge, full width. Wider: a centred dialog.
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm sm:p-4"
      onClick={() => { if (closeOnOverlay) onClose(); }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className={`bg-white rounded-t-xl sm:rounded-lg shadow-elevated w-full ${widths[size]} max-h-[92dvh] sm:max-h-[90vh] overflow-hidden flex flex-col outline-none`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id="modal-title" className="text-base font-semibold text-ink break-words">{title}</h2>
            {subtitle && <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1 -m-1 rounded"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-4 sm:px-5 py-4 overflow-y-auto flex-1 min-h-0">
          {children}
        </div>
        {footer && (
          <div className="px-4 sm:px-5 py-3 border-t border-slate-200 bg-slate-50 flex flex-wrap items-center justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/** Yes/No confirmation modal — replaces window.confirm() everywhere. Tone
 *  drives the confirm-button style and the little icon in the header. */
export function ConfirmModal({
  open, onClose, onConfirm, title, message, confirmLabel = "Confirm",
  cancelLabel = "Cancel", tone = "primary", busy = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "primary" | "danger" | "warning";
  busy?: boolean;
}) {
  const toneCls =
    tone === "danger" ? "btn-danger"
    : tone === "warning" ? "btn-accent"
    : "btn-primary";

  async function handleConfirm() {
    // Support async confirm handlers — the modal stays open while busy.
    await onConfirm();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      closeOnOverlay={!busy}
      footer={
        <>
          <button className="btn-ghost" onClick={onClose} disabled={busy}>{cancelLabel}</button>
          <button className={toneCls} onClick={handleConfirm} disabled={busy}>
            {busy ? (<><span className="spinner" aria-hidden="true" /> Working…</>) : confirmLabel}
          </button>
        </>
      }
    >
      <div className="flex gap-3 text-sm text-slate-700">
        {tone !== "primary" && (
          <AlertTriangle
            className={tone === "danger" ? "h-5 w-5 text-rose-500 shrink-0" : "h-5 w-5 text-amber-500 shrink-0"}
            aria-hidden="true"
          />
        )}
        <div>{message}</div>
      </div>
    </Modal>
  );
}

export interface PromptField {
  name: string;
  label: string;
  placeholder?: string;
  initialValue?: string;
  type?: "text" | "email" | "url" | "number";
  required?: boolean;
  pattern?: string;
  minLength?: number;
  helpText?: string;
  /** Client-side validator run before submit. Return an error string or null. */
  validate?: (value: string, all: Record<string, string>) => string | null;
  /** Optional per-input transform (e.g. slug-case). */
  transform?: (raw: string) => string;
}

/** Multi-field prompt modal — replaces window.prompt() and supports validation.
 *  onSubmit receives a `{name -> value}` map and may return a rejection to keep
 *  the modal open with an error message. */
export function PromptModal({
  open, onClose, onSubmit, title, description, fields,
  submitLabel = "Save", cancelLabel = "Cancel",
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: Record<string, string>) => void | Promise<void>;
  title: string;
  description?: ReactNode;
  fields: PromptField[];
  submitLabel?: string;
  cancelLabel?: string;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Reset state whenever the modal opens with new fields.
  useEffect(() => {
    if (open) {
      const init: Record<string, string> = {};
      for (const f of fields) init[f.name] = f.initialValue ?? "";
      setValues(init);
      setErrors({});
      setSubmitError(null);
      setBusy(false);
    }
  }, [open, fields]);

  function validateAll(): boolean {
    const next: Record<string, string | null> = {};
    for (const f of fields) {
      const v = values[f.name] ?? "";
      if (f.required && v.trim().length === 0) {
        next[f.name] = `${f.label} is required.`;
      } else if (f.minLength && v.length < f.minLength) {
        next[f.name] = `${f.label} must be at least ${f.minLength} characters.`;
      } else if (f.pattern && v.length > 0 && !new RegExp(`^${f.pattern}$`).test(v)) {
        next[f.name] = `${f.label} has an invalid format.`;
      } else if (f.validate) {
        next[f.name] = f.validate(v, values);
      } else {
        next[f.name] = null;
      }
    }
    setErrors(next);
    return Object.values(next).every((e) => !e);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateAll()) return;
    setBusy(true);
    setSubmitError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      closeOnOverlay={!busy}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>{cancelLabel}</button>
          <button type="submit" form="prompt-modal-form" className="btn-primary" disabled={busy}>
            {busy ? (<><span className="spinner" aria-hidden="true" /> Saving…</>) : submitLabel}
          </button>
        </>
      }
    >
      <form id="prompt-modal-form" onSubmit={submit} className="space-y-3" noValidate>
        {description && <div className="text-sm text-slate-600 mb-3">{description}</div>}
        {fields.map((f) => {
          const err = errors[f.name];
          const id = `pm-${f.name}`;
          return (
            <div key={f.name}>
              <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">
                {f.label} {f.required && <span className="text-rose-600" aria-hidden="true">*</span>}
              </label>
              <input
                id={id}
                type={f.type || "text"}
                className={`input ${err ? "!border-rose-400 !ring-rose-400/40" : ""}`}
                value={values[f.name] ?? ""}
                placeholder={f.placeholder}
                onChange={(e) => {
                  const raw = e.target.value;
                  const next = f.transform ? f.transform(raw) : raw;
                  setValues((v) => ({ ...v, [f.name]: next }));
                }}
                disabled={busy}
                required={f.required}
                aria-invalid={!!err}
                aria-describedby={err ? `${id}-err` : f.helpText ? `${id}-help` : undefined}
              />
              {err ? (
                <p id={`${id}-err`} className="mt-1 text-xs text-rose-700">{err}</p>
              ) : f.helpText ? (
                <p id={`${id}-help`} className="mt-1 text-xs text-slate-500">{f.helpText}</p>
              ) : null}
            </div>
          );
        })}
        {submitError && (
          <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded">
            {submitError}
          </div>
        )}
      </form>
    </Modal>
  );
}

/** Hook that gives you an imperative confirm() replacement backed by a modal.
 *  Usage: const [confirm, node] = useConfirm(); await confirm({title, message});
 *  render {node} once in the tree. */
export function useConfirm() {
  const [state, setState] = useState<null | {
    title: string; message: ReactNode; confirmLabel?: string; tone?: "primary" | "danger" | "warning";
    resolve: (ok: boolean) => void;
  }>(null);
  const [busy, setBusy] = useState(false);

  function confirm(opts: {
    title: string; message: ReactNode; confirmLabel?: string; tone?: "primary" | "danger" | "warning";
  }): Promise<boolean> {
    return new Promise((resolve) => setState({ ...opts, resolve }));
  }

  const node = state ? (
    <ConfirmModal
      open
      onClose={() => { state.resolve(false); setState(null); }}
      onConfirm={async () => {
        setBusy(true);
        state.resolve(true);
        setBusy(false);
        setState(null);
      }}
      title={state.title}
      message={state.message}
      confirmLabel={state.confirmLabel}
      tone={state.tone}
      busy={busy}
    />
  ) : null;

  return [confirm, node] as const;
}
